import { FeedbackProvider, renderFeedbackTrigger, useFeedback } from "@nurion/feedback";
import { useNurionTheme } from "@nurion/theme/react";
import {
  Component,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Bell,
  LogOut,
  MoreHorizontal,
  Pin,
  Settings,
  UserPlus,
  Video,
  VideoOff,
} from "lucide-react";

import { CreateRoomDialog } from "~/components/CreateRoomDialog";
import { CreateSpaceDialog } from "~/components/CreateSpaceDialog";
import { ConfirmDialog } from "~/components/ConfirmDialog";
import { DeviceVerification } from "~/components/DeviceVerification";
import { EmbedPortalProvider, useRegisterEmbedRoot } from "~/components/EmbedPortalContext";
import { IconTooltip } from "~/components/IconTooltip";
import { EncryptionBadge } from "~/components/EncryptionBadge";
import { FaviconBadge } from "~/components/FaviconBadge";
import { InviteDialog } from "~/components/InviteDialog";
import { buildKeyBackupRestoreMode, KeyBackupSetup } from "~/components/KeyBackupSetup";
import { LeaveRoomDialog } from "~/components/LeaveRoomDialog";
import { NewDmDialog } from "~/components/NewDmDialog";
import { NotificationSettings } from "~/components/NotificationSettings";
import { PermissionBanner } from "~/components/PermissionBanner";
import { PinnedPanel } from "~/components/PinnedPanel";
import { RoomSettings } from "~/components/RoomSettings";
import { SettingsDialog } from "~/components/SettingsDialog";
import { ActivityViews } from "~/components/ActivityViews";
import { SearchPanel } from "~/components/SearchPanel";
import { Sidebar } from "~/components/Sidebar";
import { ShortcutDialog } from "~/components/ShortcutDialog";
import { SyncStatusBanner } from "~/components/SyncStatusBanner";
import { TabTitle } from "~/components/TabTitle";
import { ThreadPanel } from "~/components/ThreadPanel";
import { Timeline } from "~/components/Timeline";
import { TopBar } from "~/components/TopBar";
import { VerificationBanner } from "~/components/VerificationBanner";
import { VerificationLauncherProvider } from "~/components/VerificationLauncherContext";
import { MobileShell } from "~/components/mobile/MobileShell";
import { MatrixProvider, useMatrix, useMatrixRooms } from "~/context/MatrixContext";
import { useEmbedRuntime } from "~/context/EmbedRuntimeContext";
import { useFormFactor } from "~/hooks/useFormFactor";
import { CHAT_PRODUCTION_PUBLIC_URL, openExternal } from "~/lib/platform";
import { captureSdkException } from "~/lib/telemetry";
import { ToastProvider } from "~/context/ToastContext";
import { useToast } from "~/context/ToastContext";
import { useRooms } from "~/hooks/useRooms";
import { useCrypto } from "~/hooks/useCrypto";
import { useNotifications } from "~/hooks/useNotifications";
import { usePinned } from "~/hooks/usePinned";
import { POWER_LEVEL_ADMIN } from "~/hooks/useRoomSettings";
import { useUpdater } from "~/hooks/useUpdater";
import type { QuoteComposerHandle } from "~/lib/quote";
import {
  restoreKeyBackupFromVerifiedDevice,
  getRoomNotificationLevel,
  getMembers,
  isRoomEncrypted as matrixIsRoomEncrypted,
  requestOpenIdToken,
  searchEvents as matrixSearchEvents,
  setRoomNotificationLevel as matrixSetRoomNotificationLevel,
  subscribeTotalUnread,
  type Client,
  type RoomNotificationLevel,
  type RoomSummary,
  type SearchResultsPage,
  type TimelineEvent,
  type UnreadCounts,
} from "~/lib/matrix";
import { cachedOpenIdProvider } from "~/lib/meet-instant";
import { useMeetingActions } from "~/hooks/useMeetingActions";
import { formatMessageTime } from "~/lib/message-utils";
import { isMobileApp, platform, type Platform } from "~/lib/platform";
import { isShortcutDialogKey } from "~/lib/shortcut-events";
import { rememberLaunchHash, takeRememberedLaunchHash, useLaunchRoomLink } from "~/lib/launch-link";
import { linkedRoomId, RoomLinkContext, type OpenRoomLink } from "~/lib/permalink";
import type { CryptoBootstrapError } from "~/lib/post-login-pipeline";
import { ICON_STROKE } from "~/lib/icon";

// Web-only `/oidc/callback` handler. On Tauri the deeplink listener in
// MatrixProvider does the equivalent. After a same-tab redirect the SPA
// boots at `/oidc/callback?code=…&state=…`; this component extracts the
// query and drives `completeLogin` which restores the persisted PKCE
// state and exchanges the authorization code for tokens.
export function WebCallbackHandler() {
  const { completeLogin } = useMatrix();

  useEffect(() => {
    if (platform !== "web") return;
    if (window.location.pathname !== "/oidc/callback") return;
    const query = window.location.search.replace(/^\?/, "");
    window.history.replaceState({}, "", "/" + takeRememberedLaunchHash());
    if (!query) return;
    const params = new URLSearchParams(query);
    if (!params.get("code") && !params.get("error")) return;
    void completeLogin(query);
  }, [completeLogin]);

  return null;
}

export function LoginScreen() {
  const { signIn, forceReloginBanner } = useMatrix();
  const triggered = useRef(false);
  const isStub = import.meta.env.VITE_CHAT_MATRIX_BINDING === "stub";
  const isMobile = useFormFactor() === "mobile";

  useEffect(() => {
    if (isStub) return;
    if (triggered.current) return;
    if (platform === "web" && window.location.pathname === "/oidc/callback") return;
    if (forceReloginBanner) return;
    // On mobile the user taps an explicit CTA rather than being thrown
    // straight into the system browser — opening it unprompted on a phone
    // reads as a crash. The handoff is driven by MobileLoginScreen.
    if (isMobile) return;
    triggered.current = true;
    rememberLaunchHash();
    void Promise.resolve(signIn()).catch(() => {});
  }, [signIn, isStub, forceReloginBanner, isMobile]);

  if (forceReloginBanner) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-6 px-8 text-center">
        <div className="max-w-sm space-y-3">
          <span className="text-[10px] uppercase tracking-widest text-muted-quiet">
            Sign in again
          </span>
          <p className="text-sm font-light text-foreground/70">
            We&apos;ve upgraded encryption on this device. Sign in again to continue — your rooms
            and history are safe.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            triggered.current = true;
            void Promise.resolve(signIn()).catch(() => {});
          }}
          className="rounded-lg bg-foreground px-5 py-2.5 text-sm font-medium text-background outline-none transition-opacity hover:opacity-80 focus-visible:shadow-neon-cyan"
        >
          Sign in
        </button>
      </div>
    );
  }

  if (isStub) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-6 px-8">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-light text-foreground">Sign in</h1>
          <p className="text-sm font-light text-muted-foreground">
            Sign in to connect to your homeserver
          </p>
        </div>
        <button
          type="button"
          onClick={() => void Promise.resolve(signIn()).catch(() => {})}
          className="rounded-lg bg-foreground px-5 py-2.5 text-sm font-medium text-background outline-none transition-opacity hover:opacity-80 focus-visible:shadow-neon-cyan"
        >
          Sign in
        </button>
      </div>
    );
  }

  if (isMobile) return <MobileLoginScreen onSignIn={signIn} />;

  return <ConnectingScreen label="Redirecting to sign in…" />;
}

export function MobileLoginScreen({ onSignIn }: { onSignIn: () => Promise<void> | void }) {
  const [redirecting, setRedirecting] = useState(false);

  const handleSignIn = useCallback(() => {
    setRedirecting(true);
    rememberLaunchHash();
    void Promise.resolve(onSignIn())
      .catch(() => undefined)
      .finally(() => setRedirecting(false));
  }, [onSignIn]);

  // Re-enable the CTA when the user returns to the app. `openExternal`
  // resolves as soon as the browser opens, so backing out of MAS without
  // signing in would otherwise leave the button disabled until restart.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === "visible") setRedirecting(false);
    }
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  return (
    <div className="flex h-full flex-col items-center justify-center gap-8 px-8 text-center chat-safe-top chat-safe-bottom chat-safe-left chat-safe-right">
      <span className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
        Nurion Chat
      </span>
      <button
        type="button"
        onClick={handleSignIn}
        disabled={redirecting}
        className="flex h-11 w-full max-w-xs items-center justify-center rounded-lg bg-foreground px-5 text-sm font-medium text-background outline-none transition-opacity hover:opacity-80 focus-visible:shadow-neon-cyan disabled:opacity-50"
      >
        Sign in
      </button>
      <div
        role="status"
        aria-live="polite"
        className="min-h-4 text-xs font-light text-muted-foreground"
      >
        {redirecting ? "Opening your browser to sign in securely…" : ""}
      </div>
    </div>
  );
}

export function KeychainUnavailableScreen() {
  const { retryKeychain } = useMatrix();
  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 px-8 text-center">
      <div className="max-w-md space-y-3">
        <span className="text-[10px] uppercase tracking-widest text-muted-quiet">
          Keychain required
        </span>
        <p className="text-sm font-light text-foreground/70">
          Nurion Chat requires your OS keychain to be available. On Linux, ensure
          gnome-keyring-daemon or kwallet is running. On macOS, unlock your Keychain Access. On
          Windows, ensure Credential Manager is enabled. After fixing, click Retry.
        </p>
      </div>
      <button
        type="button"
        onClick={() => void retryKeychain?.()}
        className="rounded-lg bg-foreground px-5 py-2.5 text-sm font-medium text-background outline-none transition-opacity hover:opacity-80 focus-visible:shadow-neon-cyan"
      >
        Retry
      </button>
    </div>
  );
}

export function ConnectingScreen({ label }: { label: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex h-full flex-col items-center justify-center gap-3 text-center"
    >
      <span className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
        Nurion Chat
      </span>
      <p className="text-base font-light text-foreground/70">{label}</p>
    </div>
  );
}

export function ErrorScreen() {
  const { recoverToLogin } = useMatrix();
  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 px-8 text-center">
      <div className="max-w-sm space-y-3">
        <p className="text-sm font-medium text-destructive-text">Something went wrong</p>
        <p className="text-xs font-light text-muted-foreground">
          Your session can&apos;t be restored. Sign in again to continue.
        </p>
      </div>
      <button
        type="button"
        onClick={() => void recoverToLogin()}
        className="rounded-lg bg-foreground px-5 py-2.5 text-sm font-medium text-background outline-none transition-opacity hover:opacity-80 focus-visible:shadow-neon-cyan"
      >
        Sign in again
      </button>
    </div>
  );
}

export function useShellGateScreen({
  connectingLabel,
  syncingLabel,
}: {
  connectingLabel: string;
  syncingLabel: string;
}): ReactNode | null {
  const { state, keychainError, roomsReady } = useMatrix();
  if (keychainError != null) return <KeychainUnavailableScreen />;
  if (state === "loading") return <ConnectingScreen label={connectingLabel} />;
  if (state === "login") return <LoginScreen />;
  if (state === "error") return <ErrorScreen />;
  if (state === "syncing" && !roomsReady) return <ConnectingScreen label={syncingLabel} />;
  if (state === "synced" && !roomsReady) return <ConnectingScreen label={syncingLabel} />;
  return null;
}

export { SyncStatusBanner };

const SIGN_OUT_CONFIRM_BODY =
  "Signing out clears your unsent drafts and scheduled messages on this device. " +
  "You can sign back in any time — but reading encrypted messages here again needs " +
  "another signed-in device or your recovery key.";

export function useSignOutConfirm() {
  const { signOut } = useMatrix();
  const [open, setOpen] = useState(false);
  return {
    requestSignOut: () => setOpen(true),
    confirmNode: open ? (
      <ConfirmDialog
        title="Sign out?"
        body={SIGN_OUT_CONFIRM_BODY}
        confirmLabel="Sign out"
        onConfirm={() => {
          setOpen(false);
          void signOut();
        }}
        onCancel={() => setOpen(false)}
      />
    ) : null,
  };
}

export function accountMenuFeedbackAction(
  platformArg: Platform,
  openExternalFn: (url: string) => void | Promise<unknown>,
  available: boolean | undefined,
  start: () => void,
): { onSelect: () => void } | undefined {
  if (platformArg === "tauri") {
    return {
      onSelect: () => void openExternalFn(`${CHAT_PRODUCTION_PUBLIC_URL}/?feedback=open`),
    };
  }
  return available === true ? { onSelect: () => void start() } : undefined;
}

export function useWebFeedbackOpen(
  platformArg: Platform,
  available: boolean | undefined,
  start: () => void,
) {
  const startedRef = useRef(false);
  useEffect(() => {
    if (platformArg === "tauri") return;
    if (available !== true) return;
    if (startedRef.current) return;
    if (new URLSearchParams(window.location.search).get("feedback") !== "open") return;
    startedRef.current = true;
    void start();
    const params = new URLSearchParams(window.location.search);
    params.delete("feedback");
    const query = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`,
    );
  }, [platformArg, available, start]);
}

function Shell() {
  const { displayName, activeView, activeRoomId, client, jumpToEvent, setActiveRoomId, state } =
    useMatrix();
  const rooms = useMatrixRooms();
  const gateScreen = useShellGateScreen({
    connectingLabel: "Connecting…",
    syncingLabel: "Syncing your messages…",
  });
  const visibleRooms = useRooms();
  const { consoleUrl } = useEmbedRuntime();
  const feedback = useFeedback();
  const feedbackAction = accountMenuFeedbackAction(
    platform,
    openExternal,
    feedback.available,
    () => void feedback.start(),
  );
  useWebFeedbackOpen(platform, feedback.available, () => void feedback.start());
  const [createOpen, setCreateOpen] = useState(false);
  const [createSpaceOpen, setCreateSpaceOpen] = useState(false);
  const [newDmOpen, setNewDmOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [accountSettingsOpen, setAccountSettingsOpen] = useState(false);
  const { requestSignOut, confirmNode } = useSignOutConfirm();
  const [leaveRoom, setLeaveRoom] = useState<RoomSummary | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [shortcutDialogOpen, setShortcutDialogOpen] = useState(false);
  const [searchInitialQuery, setSearchInitialQuery] = useState<string | undefined>(undefined);
  // Unread aggregate. Drives the tab title (`highlight`) and the
  // favicon badge (`total`). Read once at the top of the shell so
  // both renderers see the same snapshot — no chance of the title
  // + favicon drifting apart.
  const [unread, setUnread] = useState<UnreadCounts>({ total: 0, highlight: 0 });
  useEffect(() => {
    if (!client) return;
    const unsub = subscribeTotalUnread(client, (next) => setUnread(next));
    return () => {
      unsub();
    };
  }, [client]);

  // Desktop notifications. The hook owns the subscription fan-out
  // over every joined room, the first-login permission prompt, and
  // the banner-state derivation.
  const { permission, bannerState, dismissBanner, enableFromBanner } = useNotifications(
    client,
    rooms,
  );

  // Tauri auto-update toast (#1849). No-op on web. Mounted at the
  // signed-in shell rather than the App root so the toast only fires
  // for users who have actually opened the app to a usable state —
  // the alternative (mounting at App) would race with the splash and
  // surface "Restart to update" before the login screen appears.
  const updateOffer = useUpdater();

  // Global Cmd/Ctrl+K — opens the search dialog. Mounted at the
  // Shell (not outside) so the shortcut only fires for signed-in
  // users. Intercepts unconditionally; the brief pins ⌘K as the
  // primary search entry-point across every chat surface, including
  // the composer, so there is no editable-target escape hatch.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k" && !e.shiftKey)) return;
      e.preventDefault();
      setSearchInitialQuery(undefined);
      setSearchOpen(true);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!isShortcutDialogKey(e)) return;
      e.preventDefault();
      setShortcutDialogOpen(true);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // `useSearch` needs a stable function identity; wrapping the
  // client-captured call site in `useCallback` keeps the debounce
  // effect inside the hook from re-firing on every render. `client`
  // is nullable while the shell sits in `loading` / `login`; the
  // fallback returns an empty page so the hook never panics.
  const runSearch = useCallback(
    async (query: string, roomId?: string, nextBatch?: string): Promise<SearchResultsPage> => {
      if (!client) return { results: [], nextBatch: null };
      return matrixSearchEvents(client, query, roomId, nextBatch);
    },
    [client],
  );

  const onJump = useCallback(
    (roomId: string, eventId: string) => {
      jumpToEvent({ roomId, eventId });
      setSearchOpen(false);
    },
    [jumpToEvent],
  );

  const openRoomLink = useCallback<OpenRoomLink>(
    (link) => {
      const roomId = linkedRoomId(visibleRooms, link);
      if (!roomId) return false;
      if (link.eventId) jumpToEvent({ roomId, eventId: link.eventId });
      else setActiveRoomId(roomId);
      return true;
    },
    [visibleRooms, jumpToEvent, setActiveRoomId],
  );
  useLaunchRoomLink(openRoomLink, gateScreen === null && state === "synced");

  if (gateScreen) return gateScreen;

  const activeRoom = visibleRooms.find((r) => r.id === activeRoomId) ?? null;
  // The right-panel settings only make sense when a room is active
  // and the user is viewing it. Switching to Saved or away from the
  // room auto-closes the panel.
  const isRoomView = activeView === "room";
  const settingsVisible = settingsOpen && activeRoom !== null && isRoomView;
  const inviteVisible = inviteOpen && activeRoom !== null;

  // synced
  const shell = (
    <CryptoHost>
      <div className="flex h-full flex-col">
        <TabTitle highlight={unread.highlight} />
        <FaviconBadge total={unread.total} />
        <PermissionBanner
          visible={bannerState === "denied"}
          blocked={permission === "denied"}
          onEnable={enableFromBanner}
          onDismiss={dismissBanner}
        />
        <SyncStatusBanner />
        {/* Sibling of the three-column row, not nested in the content column. */}
        <TopBar
          update={updateOffer ?? undefined}
          onCreateSpace={() => {
            // Console links are derived from configured runtime origin, not Matrix room state.
            void openExternal(new URL("/team/new", consoleUrl).toString());
          }}
          onCreateSubSpace={() => setCreateSpaceOpen(true)}
        />
        <div className="flex flex-1 min-h-0">
          <Sidebar
            onCreateRoom={() => setCreateOpen(true)}
            onNewDm={() => setNewDmOpen(true)}
            onOpenSearch={(query) => {
              setSearchInitialQuery(query.trim().length > 0 ? query : undefined);
              setSearchOpen(true);
            }}
            displayName={displayName}
            onOpenSettings={() => setAccountSettingsOpen(true)}
            onSignOut={requestSignOut}
            logoutHref="/logout"
            feedbackAction={feedbackAction}
          />
          <main className="flex flex-1 min-w-0">
            {isRoomView ? (
              <RoomView
                displayName={displayName}
                activeRoom={activeRoom}
                onOpenSettings={() => setSettingsOpen(true)}
                onOpenLeave={(room) => setLeaveRoom(room)}
                onOpenInvite={() => setInviteOpen(true)}
                onOpenSearch={(query) => {
                  setSearchInitialQuery(query);
                  setSearchOpen(true);
                }}
                suppressThreadPanel={settingsVisible}
              />
            ) : (
              <ActivityViews />
            )}
            {settingsVisible && activeRoom && (
              // `key` forces a fresh mount per room so in-flight edits
              // on one room can't bleed into another. The ref-based
              // remote-sync in `RoomSettings` still covers the
              // "same room, server value changes" path.
              //
              // Mutual exclusion with `ThreadPanel`: the room-settings
              // pane and the thread panel both occupy the same right-
              // edge column. When settings is open we suppress the
              // thread panel via `RoomView.suppressThreadPanel` so we
              // never render two right-side aside columns at once. The
              // open thread itself isn't dismissed — it reappears when
              // the user closes settings.
              <RoomSettings
                key={activeRoom.id}
                room={activeRoom}
                onClose={() => setSettingsOpen(false)}
              />
            )}
          </main>
        </div>
        {createOpen && <CreateRoomDialog onClose={() => setCreateOpen(false)} />}
        {createSpaceOpen && <CreateSpaceDialog onClose={() => setCreateSpaceOpen(false)} />}
        {newDmOpen && <NewDmDialog onClose={() => setNewDmOpen(false)} />}
        {leaveRoom && <LeaveRoomDialog room={leaveRoom} onClose={() => setLeaveRoom(null)} />}
        {inviteVisible && activeRoom && (
          <InviteDialog
            roomId={activeRoom.id}
            roomName={activeRoom.name}
            onClose={() => setInviteOpen(false)}
          />
        )}
        <SearchPanel
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          search={runSearch}
          rooms={visibleRooms}
          onJump={onJump}
          initialQuery={searchInitialQuery}
        />
        {shortcutDialogOpen && <ShortcutDialog onClose={() => setShortcutDialogOpen(false)} />}
        {accountSettingsOpen && <SettingsDialog onClose={() => setAccountSettingsOpen(false)} />}
        {confirmNode}
      </div>
    </CryptoHost>
  );
  return <RoomLinkContext.Provider value={openRoomLink}>{shell}</RoomLinkContext.Provider>;
}

/**
 * Hosts the E2EE-surfacing UI: incoming verification banner + active
 * SAS dialog + key-backup save/restore dialogs. Lives at the top of
 * the synced shell so the banner sits above the chrome and the
 * dialogs overlay everything.
 *
 * All three surfaces are conditional:
 *   - Banner: visible while `incomingRequest` is non-null.
 *   - Dialog: visible while a live `session` exists.
 *   - Save dialog: visible when `pendingRecoveryKey` is set (first
 *     device of the account).
 *   - Restore dialog: visible when `needsRecovery` is true (new
 *     device with a backup available).
 */
export function CryptoHost({ children }: { children: ReactNode }) {
  const {
    client,
    userId,
    pendingRecoveryKey,
    dismissRecoveryKey,
    needsRecovery,
    recoveryView,
    markRecovered,
    skipRecoveryForSession,
    cryptoError,
    retryCryptoBootstrap,
    canUnlockHistory,
  } = useMatrix();
  const [retrying, setRetrying] = useState(false);
  const {
    incomingRequest,
    request,
    session,
    qrSession,
    verificationError,
    sessionState,
    qrSessionState,
    completedVerification,
    acceptIncoming,
    declineIncoming,
    dismissIncoming,
    confirmMatch,
    startSas,
    startQr,
    scanQrCode,
    confirmQr,
    cancelSession,
    clearSession,
    startVerification,
  } = useCrypto(client, userId);
  const [deviceRestoreStatus, setDeviceRestoreStatus] = useState<
    "idle" | "verified" | "restoring" | "restored" | "failed"
  >("idle");
  const [deviceRestoreError, setDeviceRestoreError] = useState<string | null>(null);
  const recoveryVerifyRef = useRef(false);
  const skipRecovery = useCallback(() => {
    skipRecoveryForSession();
    void retryCryptoBootstrap({ skipRecoveryGate: true });
  }, [skipRecoveryForSession, retryCryptoBootstrap]);
  const [verificationInitialMode, setVerificationInitialMode] = useState<"sas" | "qr">(
    isMobileApp ? "qr" : "sas",
  );
  const handledVerificationTokenRef = useRef<number | null>(null);

  useEffect(() => {
    handledVerificationTokenRef.current = null;
  }, [client, userId]);

  const handleRetry = useCallback(async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      await retryCryptoBootstrap();
    } finally {
      setRetrying(false);
    }
  }, [retryCryptoBootstrap, retrying]);

  useEffect(() => {
    if (!client || !needsRecovery || !completedVerification) return;
    if (completedVerification.client !== client || completedVerification.userId !== userId) return;
    if (handledVerificationTokenRef.current === completedVerification.token) return;
    handledVerificationTokenRef.current = completedVerification.token;
    recoveryVerifyRef.current = false;
    let cancelled = false;
    void (async () => {
      setDeviceRestoreStatus("verified");
      setDeviceRestoreError(null);
      clearSession();
      await new Promise((resolve) => window.setTimeout(resolve, 300));
      if (cancelled) return;
      setDeviceRestoreStatus("restoring");
      try {
        await restoreKeyBackupFromVerifiedDevice(client);
        if (cancelled) return;
        setDeviceRestoreStatus("restored");
      } catch (err) {
        if (cancelled) return;
        setDeviceRestoreError(
          err instanceof Error ? err.message : "Use your recovery key instead.",
        );
        setDeviceRestoreStatus("failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clearSession, client, completedVerification, needsRecovery, userId]);

  const verificationDialogOpen = Boolean(
    incomingRequest || request || session || qrSession || verificationError,
  );

  return (
    <VerificationLauncherProvider value={{ startVerification }}>
      {children}
      {cryptoError && (
        <CryptoErrorBanner error={cryptoError} onRetry={handleRetry} retrying={retrying} />
      )}
      {incomingRequest && (
        <VerificationBanner
          request={incomingRequest}
          ownUserId={userId}
          thisDeviceLocked={canUnlockHistory}
          onAccept={() => void acceptIncoming()}
          onDismiss={() => {
            void declineIncoming();
            dismissIncoming();
          }}
        />
      )}
      {(request || session || qrSession || verificationError) && (
        <DeviceVerification
          session={session}
          request={request}
          qrSession={qrSession}
          state={sessionState}
          qrState={qrSessionState}
          error={verificationError}
          onClose={() => {
            // Closing a live session cancels it; closing a terminal
            // session is a no-op at the session level. `useCrypto`
            // clears local state either way.
            if (
              sessionState !== "done" &&
              sessionState !== "cancelled" &&
              sessionState !== "failed" &&
              qrSessionState !== "done" &&
              qrSessionState !== "cancelled" &&
              qrSessionState !== "failed"
            ) {
              void cancelSession();
            }
            clearSession();
            if (recoveryVerifyRef.current) {
              recoveryVerifyRef.current = false;
              if (needsRecovery && sessionState !== "done" && qrSessionState !== "done") {
                skipRecovery();
              }
            }
          }}
          onConfirm={() => void confirmMatch()}
          onReject={() => void cancelSession()}
          onStartSas={startSas}
          onStartQr={startQr}
          onScanQrCode={scanQrCode}
          onConfirmQr={confirmQr}
          onRetry={() => {
            const target = request ?? session ?? qrSession;
            clearSession();
            if (target?.otherDeviceId) {
              void startVerification(target.otherUserId, target.otherDeviceId);
            }
          }}
          initialMode={verificationInitialMode}
        />
      )}
      {pendingRecoveryKey && (
        <KeyBackupSetup
          mode={{
            kind: "save",
            recoveryKey: pendingRecoveryKey,
            onConfirm: dismissRecoveryKey,
          }}
        />
      )}
      {needsRecovery && client && !verificationDialogOpen && (
        <KeyBackupSetup
          mode={{
            ...buildKeyBackupRestoreMode(
              client,
              () => {
                markRecovered();
                void retryCryptoBootstrap();
              },
              skipRecovery,
              "skip-only",
            ),
            recoveryView,
            onVerifyDevice: async (deviceId, method = "qr") => {
              if (!userId) return;
              setDeviceRestoreStatus("idle");
              setDeviceRestoreError(null);
              setVerificationInitialMode(method === "qr" ? "qr" : "sas");
              try {
                await startVerification(userId, deviceId);
                recoveryVerifyRef.current = true;
              } catch (err) {
                setDeviceRestoreError(
                  err instanceof Error ? err.message : "Could not start verification. Try again.",
                );
                setDeviceRestoreStatus("failed");
              }
            },
            deviceRestoreStatus,
            deviceRestoreError,
          }}
        />
      )}
    </VerificationLauncherProvider>
  );
}

export function CryptoErrorBanner({
  error,
  onRetry,
  retrying,
}: {
  error: CryptoBootstrapError;
  onRetry: () => void;
  retrying: boolean;
}) {
  const copy = cryptoErrorCopy(error.stage);
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "var(--chat-space-4)",
        padding: "var(--chat-space-3) var(--chat-space-4)",
        background: "var(--chat-surface-raised)",
        color: "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        borderBottom: "1px solid var(--chat-surface-border)",
      }}
    >
      <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        <span
          style={{
            color: "var(--chat-state-danger)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
            fontWeight: 500,
          }}
        >
          {copy.title}
        </span>
        <span
          style={{
            color: "var(--chat-fg-muted)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
          }}
        >
          {copy.detail}
        </span>
      </div>
      <button
        type="button"
        className="chat-button"
        onClick={onRetry}
        disabled={retrying}
        style={{
          flexShrink: 0,
          height: 32,
          padding: "0 var(--chat-space-3)",
          border: "none",
          borderRadius: "var(--chat-radius-sm)",
          background: "var(--chat-surface-overlay)",
          color: retrying ? "var(--chat-fg-muted)" : "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          lineHeight: "var(--chat-type-body-sm-line)",
          fontWeight: 500,
          cursor: retrying ? "default" : "pointer",
        }}
      >
        {retrying ? "Retrying" : "Retry"}
      </button>
    </div>
  );
}

function cryptoErrorCopy(stage: CryptoBootstrapError["stage"]): {
  title: string;
  detail: string;
} {
  if (stage === "initCrypto") {
    return {
      title: "Encryption setup failed",
      detail: "Could not start encryption. Retry setup to unlock encrypted rooms.",
    };
  }
  if (stage === "bootstrapCrossSigning") {
    return {
      title: "Cross-signing setup failed",
      detail: "Retry setup to verify this device.",
    };
  }
  return {
    title: "Key backup setup failed",
    detail: "Retry setup or enter your recovery key when prompted.",
  };
}

// Room view: renders the virtualised <Timeline> for the active room,
// or a welcome placeholder when no room is selected. Pending jumps
// (from the Saved view and the global Search dialog) hand off to the
// Timeline, which owns scrolling and the highlight flash and clears
// the context's pending jump once applied.
export function RoomView({
  displayName,
  activeRoom,
  onOpenSettings,
  onOpenLeave,
  onOpenInvite,
  onOpenSearch,
  suppressThreadPanel,
}: {
  displayName: string | null;
  activeRoom: RoomSummary | null;
  onOpenSettings: () => void;
  onOpenLeave: (room: RoomSummary) => void;
  onOpenInvite: () => void;
  onOpenSearch: (query: string) => void;
  /** When true the thread panel is hidden — used by the shell to
   *  prevent room-settings and thread-panel from sharing the right
   *  edge. The open thread is NOT dismissed; it returns when the
   *  flag flips back to false. */
  suppressThreadPanel: boolean;
}) {
  const {
    client,
    activeRoomId,
    pendingJump,
    clearPendingJump,
    activeThreadRoot,
    openThread,
    closeThread,
    userId,
    cryptoReady,
  } = useMatrix();
  const mainComposerRef = useRef<QuoteComposerHandle | null>(null);
  const threadComposerRef = useRef<QuoteComposerHandle | null>(null);
  const [roomEncrypted, setRoomEncrypted] = useState<boolean | null>(null);

  // Per-room opener: closes over `activeRoomId` so the thread store
  // is always stamped with the room the user actually opened from.
  // Stable across renders for the lifetime of the active room — the
  // Timeline's prop diff stays cheap.
  const handleOpenThread = useCallback(
    (root: TimelineEvent) => {
      if (!activeRoomId) return;
      openThread({ roomId: activeRoomId, root });
    },
    [openThread, activeRoomId],
  );

  useEffect(() => {
    if (!client || !activeRoomId) {
      setRoomEncrypted(null);
      return;
    }
    let cancelled = false;
    setRoomEncrypted(null);
    void matrixIsRoomEncrypted(client, activeRoomId)
      .then((encrypted) => {
        if (!cancelled) setRoomEncrypted(encrypted);
      })
      .catch((err) => {
        console.warn("isRoomEncrypted failed", err);
        if (!cancelled) setRoomEncrypted(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client, activeRoomId]);

  if (!activeRoomId || !client) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-sm font-light text-muted-foreground">
          Welcome, <span className="text-foreground/70">{displayName ?? "friend"}</span>
          {activeRoomId ? null : " — pick a room to start reading."}
        </p>
      </div>
    );
  }

  const placeholder = activeRoom
    ? activeRoom.isDirect
      ? `Message @${activeRoom.name}`
      : `Message #${activeRoom.name}`
    : "Message";

  // Only forward the pending jump to the timeline when it targets the
  // currently-active room. During an org switch or stale update the
  // pair can briefly mismatch; the guard stops the timeline from
  // scrolling to an event in a room it is not showing.
  const jumpForThisRoom = pendingJump && pendingJump.roomId === activeRoomId ? pendingJump : null;

  // Mirror the embed's thread-panel rule: only render when the open
  // thread belongs to the currently-shown room. `MatrixContext` drops
  // the active thread on room switch / org switch already; the guard
  // here is the same-tick render safety net. `suppressThreadPanel`
  // is the shell's mutual-exclusion override for the room-settings
  // right column.
  const showThreadPanel =
    !suppressThreadPanel && activeThreadRoot !== null && activeThreadRoot.roomId === activeRoomId;
  const quoteComposerRef = showThreadPanel ? threadComposerRef : mainComposerRef;
  const removedBySelf = activeRoom?.removedBySelf ?? null;
  const archived = activeRoom?.archived !== null && activeRoom?.archived !== undefined;
  const isAdmin =
    activeRoom && client && userId
      ? (getMembers(client, activeRoom.id).find((member) => member.userId === userId)?.powerLevel ??
          0) >= POWER_LEVEL_ADMIN
      : false;
  const archivedReadOnly = archived && !isAdmin;
  const showEncryptedGate = roomEncrypted !== false && !cryptoReady;

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
        {activeRoom && (
          // React key uniqueness is per-parent — chrome + timeline must
          // be distinct within this <div> to both remount on switch.
          <RoomChrome
            key={`chrome:${activeRoom.id}`}
            client={client}
            room={activeRoom}
            encrypted={roomEncrypted === true}
            onOpenSettings={onOpenSettings}
            onOpenLeave={onOpenLeave}
            onOpenInvite={onOpenInvite}
          />
        )}
        {showEncryptedGate ? (
          <EncryptedRoomGate />
        ) : (
          <Timeline
            key={`timeline:${activeRoomId}`}
            client={client}
            roomId={activeRoomId}
            composerPlaceholder={placeholder}
            pendingJump={jumpForThisRoom}
            onJumpApplied={clearPendingJump}
            onOpenThread={handleOpenThread}
            onOpenSearch={onOpenSearch}
            archived={archived}
            archivedReadOnly={archivedReadOnly}
            composerRef={mainComposerRef}
            quoteComposerRef={quoteComposerRef}
            removedBySelf={removedBySelf}
          />
        )}
      </div>
      {showThreadPanel && activeThreadRoot && (
        <ThreadPanel
          roomId={activeThreadRoot.roomId}
          roomName={activeRoom?.name ?? ""}
          root={activeThreadRoot.root}
          onClose={closeThread}
          formatRelativeTime={formatMessageTime}
          archivedReadOnly={archivedReadOnly}
          removedReadOnly={removedBySelf !== null}
          quoteComposerRef={threadComposerRef}
        />
      )}
    </>
  );
}

function EncryptedRoomGate() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--chat-space-8)",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--chat-space-2)",
          maxWidth: 360,
          textAlign: "center",
        }}
      >
        <p
          style={{
            margin: 0,
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
          }}
        >
          Preparing encrypted room
        </p>
        <p
          style={{
            margin: 0,
            color: "var(--chat-fg-secondary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
          }}
        >
          We are unlocking this room on this device. Other rooms stay available.
        </p>
      </div>
    </div>
  );
}

/**
 * Thin chrome above the timeline — room name, topic, and the invite /
 * settings entry points. The brief's Room-header spec lives here; the
 * channel-type badge + member count rendered by the larger header
 * variant land in a later ticket.
 *
 * The notifications-settings bell (ticket #1309) is anchored in this
 * cluster so it lives alongside the rest of the right-side room
 * chrome; the popover renders via portal so its position is not
 * constrained by the chrome's flex layout.
 */
function RoomChrome({
  client,
  room,
  encrypted,
  onOpenSettings,
  onOpenLeave,
  onOpenInvite,
}: {
  client: Client;
  room: RoomSummary;
  encrypted: boolean;
  onOpenSettings: () => void;
  onOpenLeave: (room: RoomSummary) => void;
  onOpenInvite: () => void;
}) {
  // Track level locally so the popover re-renders with the new
  // selection immediately after a write — the real SDK would flush
  // this via `/sync`, but the round-trip is orders of magnitude
  // slower than the UI expectation (the popover closes on the same
  // click). Optimistic, same pattern as reactions.
  const [level, setLevel] = useState<RoomNotificationLevel>(() =>
    getRoomNotificationLevel(client, room.id),
  );
  const { userId, jumpToEvent } = useMatrix();
  const { push: pushToast } = useToast();
  const { meetOrigins } = useEmbedRuntime();
  const rooms = useMatrixRooms();
  const openIdProvider = useMemo(
    () =>
      cachedOpenIdProvider(async () => {
        if (!client) throw new Error("no matrix client");
        return requestOpenIdToken(client);
      }),
    [client],
  );
  const pinned = usePinned(client, room.id, userId);
  const {
    activeMeeting,
    canControlMeeting,
    busy: meetingBusy,
    start: handleStartMeeting,
    end: handleEndMeeting,
    join: runJoin,
    joinBusy,
  } = useMeetingActions({ client, room, userId, openIdProvider, pushToast, meetOrigins, rooms });
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [pinnedOpen, setPinnedOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const notificationsAnchorRef = useRef<HTMLButtonElement | null>(null);
  const pinnedAnchorRef = useRef<HTMLButtonElement | null>(null);
  const actionsRef = useRef<HTMLDivElement | null>(null);

  const handleLevelSelect = (next: RoomNotificationLevel) => {
    setLevel(next);
    matrixSetRoomNotificationLevel(client, room.id, next).catch((err) => {
      console.warn("setRoomNotificationLevel failed; reverting", err);
      setLevel(getRoomNotificationLevel(client, room.id));
    });
  };

  const handleUnpinAll = async () => {
    try {
      await pinned.clear();
    } catch (err) {
      console.warn("clear pinned messages failed", err);
      pushToast({ message: "Couldn't update pinned messages", tone: "danger" });
      throw err;
    }
  };

  useEffect(() => {
    if (!actionsOpen) return;
    function onPointerDown(event: PointerEvent) {
      if (actionsRef.current?.contains(event.target as Node)) return;
      setActionsOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      setActionsOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [actionsOpen]);

  return (
    <div
      data-testid="room-chrome"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-3)",
        height: "var(--chat-timeline-room-header-height)",
        padding: "0 var(--chat-space-4)",
        borderBottom: "1px solid var(--chat-surface-border)",
        background: "var(--chat-surface-app)",
        flexShrink: 0,
      }}
    >
      <div
        data-feedback-redact="true"
        style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}
      >
        <span
          style={{
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-display)",
            fontSize: "var(--chat-type-display-sm-size)",
            lineHeight: "var(--chat-type-display-sm-line)",
            fontWeight: 300,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
            {encrypted && <EncryptionBadge variant="room-header" />}
            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
              {room.isDirect ? room.name : `#${room.name}`}
            </span>
          </span>
        </span>
        {room.topic && (
          <span
            style={{
              color: "var(--chat-fg-secondary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {room.topic}
          </span>
        )}
      </div>
      {activeMeeting ? (
        <button
          type="button"
          onClick={() => void runJoin()}
          disabled={joinBusy}
          className="chat-button"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "var(--chat-space-1)",
            height: 24,
            padding: "0 var(--chat-space-2)",
            border: "none",
            borderRadius: "var(--chat-radius-full)",
            background: "var(--chat-cta-bg)",
            color: "var(--chat-cta-fg)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-xs-size)",
            lineHeight: "var(--chat-type-body-xs-line)",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          <Video aria-hidden size={12} strokeWidth={ICON_STROKE} />
          Join
        </button>
      ) : null}
      {canControlMeeting &&
        (activeMeeting ? (
          activeMeeting.hostUserId === undefined || activeMeeting.hostUserId === userId ? (
            <button
              type="button"
              className="chat-button"
              onClick={() => {
                void handleEndMeeting();
              }}
              disabled={meetingBusy}
              aria-busy={meetingBusy}
              aria-label="End meeting"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "var(--chat-space-1)",
                height: 44,
                minWidth: 44,
                padding: "0 var(--chat-space-3)",
                border: "none",
                borderRadius: "var(--chat-radius-md)",
                background: "transparent",
                color: meetingBusy ? "var(--chat-fg-muted)" : "var(--chat-fg-primary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-sm-size)",
                lineHeight: "var(--chat-type-body-sm-line)",
                fontWeight: 500,
                cursor: meetingBusy ? "wait" : "pointer",
                flexShrink: 0,
              }}
            >
              <VideoOff aria-hidden size={18} strokeWidth={ICON_STROKE} />
              {meetingBusy ? "Ending meeting\u2026" : "End meeting"}
            </button>
          ) : null
        ) : (
          <HeaderIconButton
            label="Start meeting"
            onClick={() => {
              void handleStartMeeting();
            }}
          >
            <Video size={18} strokeWidth={ICON_STROKE} />
          </HeaderIconButton>
        ))}
      <HeaderIconButton
        label={`Pinned messages, ${pinned.eventIds.length}`}
        ariaExpanded={pinnedOpen}
        ariaHaspopup="dialog"
        refCb={(el) => {
          pinnedAnchorRef.current = el;
        }}
        onClick={() => setPinnedOpen((open) => !open)}
      >
        <Pin size={18} strokeWidth={ICON_STROKE} />
      </HeaderIconButton>
      <HeaderIconButton
        label="Notification settings"
        ariaExpanded={notificationsOpen}
        ariaHaspopup="dialog"
        refCb={(el) => {
          notificationsAnchorRef.current = el;
        }}
        onClick={() => {
          // Re-read on open so a concurrent write from another
          // device surfaces before the popover renders.
          setLevel(getRoomNotificationLevel(client, room.id));
          setNotificationsOpen((open) => !open);
        }}
      >
        <Bell size={18} strokeWidth={ICON_STROKE} />
      </HeaderIconButton>
      {!room.isDirect && (
        <HeaderIconButton label={`Invite people to ${room.name}`} onClick={onOpenInvite}>
          <UserPlus size={18} strokeWidth={ICON_STROKE} />
        </HeaderIconButton>
      )}
      <div ref={actionsRef} style={{ position: "relative" }}>
        <HeaderIconButton
          label="Room actions"
          ariaExpanded={actionsOpen}
          onClick={() => setActionsOpen((open) => !open)}
        >
          <MoreHorizontal size={18} strokeWidth={ICON_STROKE} />
        </HeaderIconButton>
        {actionsOpen && (
          <RoomActionsMenu
            room={room}
            onOpenSettings={() => {
              setActionsOpen(false);
              onOpenSettings();
            }}
            onOpenLeave={() => {
              setActionsOpen(false);
              onOpenLeave(room);
            }}
          />
        )}
      </div>
      {notificationsOpen && (
        <NotificationSettings
          anchor={notificationsAnchorRef.current}
          level={level}
          onSelect={handleLevelSelect}
          onClose={() => setNotificationsOpen(false)}
        />
      )}
      {pinnedOpen && (
        <PinnedPanel
          anchor={pinnedAnchorRef.current}
          pins={pinned.pins}
          canManage={pinned.canManage}
          onJump={(eventId) => jumpToEvent({ roomId: room.id, eventId })}
          onUnpinAll={handleUnpinAll}
          onClose={() => setPinnedOpen(false)}
        />
      )}
    </div>
  );
}

function RoomActionsMenu({
  room,
  onOpenSettings,
  onOpenLeave,
}: {
  room: RoomSummary;
  onOpenSettings: () => void;
  onOpenLeave: () => void;
}) {
  const leaveLabel = room.isDirect ? "Close conversation" : "Leave channel";
  return (
    <div
      aria-label="Room actions"
      style={{
        position: "absolute",
        top: "calc(100% + var(--chat-space-1))",
        right: 0,
        zIndex: 60,
        minWidth: 176,
        padding: "var(--chat-space-1) 0",
        borderRadius: "var(--chat-radius-md)",
        border: "1px solid var(--chat-surface-raised)",
        background: "var(--chat-surface-overlay)",
        boxShadow: "var(--chat-shadow-popover)",
      }}
    >
      <RoomActionItem label="Room settings" onClick={onOpenSettings}>
        <Settings size={16} strokeWidth={ICON_STROKE} />
      </RoomActionItem>
      <RoomActionItem label={leaveLabel} onClick={onOpenLeave} danger>
        <LogOut size={16} strokeWidth={ICON_STROKE} />
      </RoomActionItem>
    </div>
  );
}

function RoomActionItem({
  label,
  onClick,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className="chat-button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        width: "100%",
        height: 32,
        padding: "0 var(--chat-space-3)",
        border: "none",
        background: "transparent",
        color: danger ? "var(--chat-state-danger)" : "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-md-size)",
        lineHeight: "var(--chat-type-body-md-line)",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span style={{ color: danger ? "var(--chat-state-danger)" : "var(--chat-fg-secondary)" }}>
        {children}
      </span>
      <span>{label}</span>
    </button>
  );
}

export function HeaderIconButton({
  label,
  onClick,
  children,
  refCb,
  ariaExpanded,
  ariaHaspopup,
  size = "32px",
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  /** Optional ref callback so a parent (e.g. RoomChrome's bell) can
   *  anchor a portal popover to the button. */
  refCb?: (el: HTMLButtonElement | null) => void;
  ariaExpanded?: boolean;
  ariaHaspopup?: "dialog" | "menu" | "listbox" | "tree" | "grid";
  size?: string;
}) {
  return (
    <IconTooltip label={label}>
      <button
        ref={refCb}
        type="button"
        onClick={onClick}
        aria-label={label}
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHaspopup}
        className="chat-button"
        style={{
          width: size,
          height: size,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          border: "none",
          borderRadius: "var(--chat-radius-md)",
          background: "transparent",
          color: "var(--chat-fg-secondary)",
          cursor: "pointer",
        }}
      >
        {children}
      </button>
    </IconTooltip>
  );
}

/**
 * `data-chat-root` is the standalone counterpart to the embed's
 * `data-nurion-chat-embed` boundary marker. Chat `fonts.css` scopes
 * typography to one of these two attributes so the chat font stack
 * only applies inside its own subtree.
 *
 * The same `<div>` is also registered with `EmbedPortalContext` so
 * dialogs / popovers / lightbox / toasts render *inside* the
 * `data-chat-root` boundary — without registration, they would
 * portal to `document.body` and lose their scoped fonts.
 */
function StandaloneChatRoot({ children }: { children: ReactNode }) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const registerEmbedRoot = useRegisterEmbedRoot();
  const { resolved } = useNurionTheme();

  // Keep `data-theme` on <html> in sync with the resolved theme so
  // chat-specific tokens (`[data-theme="dark"]` in chat.css) activate
  // alongside the shared `.dark` class that useNurionTheme manages.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", resolved);
  }, [resolved]);

  useEffect(() => {
    const el = rootRef.current;
    registerEmbedRoot(el);
    return () => {
      registerEmbedRoot(null);
    };
  }, [registerEmbedRoot]);
  return (
    <div
      ref={rootRef}
      data-chat-root
      // `position: relative` so any portal that switches its scrim to
      // `position: absolute` (the embed-mode flip in Dialog,
      // DeleteConfirmDialog, ImageLightbox) anchors against this
      // wrapper — same containment mechanism the embed uses.
      style={{ position: "relative" }}
      className="h-screen w-screen bg-background text-foreground"
    >
      {children}
    </div>
  );
}

// Standalone only: no embed host owns the widget here, so Chat supplies its own
// trigger. The embed path mounts FeedbackProvider via its host, not this component.
export function StandaloneFeedbackProvider({ children }: { children: ReactNode }) {
  const { consoleUrl } = useEmbedRuntime();
  return (
    <FeedbackProvider
      app="chat"
      apiUrl="/api"
      redactSelector="[data-feedback-redact]"
      ticketUrlTemplate={`${consoleUrl}/support?ticket={id}`}
      trigger={platform === "tauri" ? undefined : renderFeedbackTrigger}
      defaultTriggerPosition="middle-right"
    >
      {children}
    </FeedbackProvider>
  );
}

function AppShell() {
  return useFormFactor() === "mobile" ? <MobileShell /> : <Shell />;
}

export class ShellErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error) {
    captureSdkException("chat.shell.render-error", error);
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-6 px-8 text-center">
          <div className="max-w-sm space-y-3">
            <p className="text-sm font-medium text-destructive-text">Something went wrong</p>
            <p className="text-xs font-light text-muted-foreground">
              An unexpected error occurred. Reloading usually fixes it.
            </p>
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg bg-foreground px-5 py-2.5 text-sm font-medium text-background outline-none transition-opacity hover:opacity-80 focus-visible:shadow-neon-cyan"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function App() {
  return (
    <EmbedPortalProvider>
      <StandaloneFeedbackProvider>
        <MatrixProvider>
          <ToastProvider>
            <WebCallbackHandler />
            <StandaloneChatRoot>
              <ShellErrorBoundary>
                <AppShell />
              </ShellErrorBoundary>
            </StandaloneChatRoot>
          </ToastProvider>
        </MatrixProvider>
      </StandaloneFeedbackProvider>
    </EmbedPortalProvider>
  );
}
